import { sortObjectsByTranslations, pipe, isArrayWithValues } from '@/utils/fp';
import type { SelectFieldData } from '@/components/forms/inputs/SelectField/SelectField';
import { ICountryData, IHowHeardData, IProfessionData, ITimezonesData } from '@/types/types';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { PATIENT_CONSUMER_PROFESSION_ID } from '@/types/const';

type SelectOptionKeys = { label: string; value: string; infoTooltip?: string };
type TranslateFn = ReturnType<typeof usePhrase>['t'];

export const createSelectOptionsFromArray =
    <T extends object>(keys: SelectOptionKeys) =>
    (data: T[]) => {
        const isValidArrayWithSelectOptions = (
            d: T[],
            k: SelectOptionKeys,
        ): d is Array<T & Record<keyof SelectOptionKeys, unknown>> =>
            Array.isArray(d) && d.length > 0 && k.label in d[0] && k.value in d[0];

        if (isValidArrayWithSelectOptions(data, keys)) {
            return data.map((item) => {
                const option = {
                    label: item[keys.label as keyof T],
                    value: item[keys.value as keyof T],
                };
                if (keys.infoTooltip && keys.infoTooltip in item) {
                    return {
                        ...option,
                        infoTooltip: item[keys.infoTooltip as keyof T],
                    };
                }
                return option;
            });
        } else {
            return [];
        }
    };

const translateList = (list: SelectFieldData[], t: TranslateFn) =>
    list.map((item) => ({
        ...item,
        label: typeof item.label === 'string' ? item.label : (t(item.label) as string),
        useRawLabel: true,
    }));

export const generateCountriesSelectOptions = (
    countriesData: ICountryData[],
    t: TranslateFn,
    countriesSortOrder: string[][],
) => {
    const initialList = createSelectOptionsFromArray({
        label: 'phraseId',
        value: 'countryCode',
    })(countriesData);

    const sortedList = sortObjectsByTranslations(initialList, countriesSortOrder, t, 'label');

    return sortedList as SelectFieldData[];
};

export const generateProfessionsSelectOptions = (
    professionsData: IProfessionData[],
    disabledProfessionIds: number[] | undefined,
    t: TranslateFn,
    professionsSelectOrder: string[][],
    selectedCountryCode?: string,
) => {
    const countryFilteredProfessionsData = professionsData.filter((item) => {
        if (!item.countryCodes || item.countryCodes.length === 0) {
            return true;
        }
        if (selectedCountryCode) {
            return item.countryCodes.includes(selectedCountryCode);
        }
        return false;
    });

    const initialList = createSelectOptionsFromArray({
        label: 'phraseId',
        value: 'professionId',
        infoTooltip: 'descriptionPhraseId',
    })(countryFilteredProfessionsData);

    const removeDisabledProfessions = (list: Omit<SelectFieldData, 'useRawLabel'>[]) => {
        if (!isArrayWithValues(disabledProfessionIds)) return list;
        return list.filter((a) => !disabledProfessionIds?.includes(a.value as number));
    };

    const sortByTranslations = (list: Omit<SelectFieldData, 'useRawLabel'>[]) =>
        sortObjectsByTranslations(list, professionsSelectOrder, t, 'label');

    const movePatientConsumerToBottom = (list: SelectFieldData[]) => {
        const patientConsumer = list.filter((el) => el.value === PATIENT_CONSUMER_PROFESSION_ID);
        const others = list.filter((el) => el.value !== PATIENT_CONSUMER_PROFESSION_ID);
        return [...others, ...patientConsumer];
    };

    return pipe(
        initialList,
        removeDisabledProfessions,
        sortByTranslations,
        (xs) => translateList(xs, t),
        movePatientConsumerToBottom,
    );
};

export const generateHowHeardSelectOptions = (
    howHeardData: IHowHeardData[],
    t: TranslateFn,
    howHeardSelectOrder: string[][],
) => {
    const initialList = createSelectOptionsFromArray({
        label: 'phraseId',
        value: 'howHeardId',
    })(howHeardData);

    const sortedList = sortObjectsByTranslations(initialList, howHeardSelectOrder, t, 'label');

    return sortedList as SelectFieldData[];
};

export const generateTimezonesSelectOptions = (timeZonesData: ITimezonesData[]) =>
    createSelectOptionsFromArray({ label: 'name', value: 'timezoneId' })(timeZonesData);
